import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, Html } from '@react-three/drei';
import * as THREE from 'three';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { proxied } from '../services/assets';
import { ROOMS } from '../rooms';
import SplatWorld from './SplatWorld';

const ACCENT = '#9dff7d';

// ?cards=edit — live placement editor for the featured sleeves in the splat.
const CARDS_EDIT = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('cards') === 'edit';

// Shipped defaults for the featured-sleeve cards (paste tuned values from the
// editor here). Each is { pos, rotY, size }; a saved override wins per machine.
const CARD_DEFAULTS = [
  { pos: [-0.62, 1.35, -0.6], rotY: 0, size: 0.5 },
  { pos: [0, 1.35, -0.6], rotY: 0, size: 0.5 },
  { pos: [0.62, 1.35, -0.6], rotY: 0, size: 0.5 },
];

// One album face — a still sleeve or, when the track has an animated artwork
// loop and `allowVideo`, a muted VideoTexture (the record-store showcase for
// the animated-artwork rule). Clicking plays the track through the audio engine
// (single-source), and calls onPlay so the turntable can pick it up.
function AlbumArt({ track, size = 1, position = [0, 0, 0], rotation = [0, 0, 0], allowVideo = true, onPlay }) {
  const [tex, setTex] = useState(null);
  const [hover, setHover] = useState(false);
  const meshRef = useRef();
  const anim = allowVideo ? (track.artwork_animation_url || track.artwork_animated_url) : null;

  useEffect(() => {
    if (anim) {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.src = proxied(anim);
      video.loop = true; video.muted = true; video.playsInline = true; video.preload = 'auto';
      video.play().catch(() => {});
      const t = new THREE.VideoTexture(video);
      t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
      setTex(t);
      return () => { try { video.pause(); video.removeAttribute('src'); video.load(); } catch { /* noop */ } t.dispose(); };
    }
    if (!track.artwork_url) return undefined;
    const t = new THREE.TextureLoader().load(
      proxied(track.artwork_url),
      (img) => { img.colorSpace = THREE.SRGBColorSpace; img.anisotropy = 8; img.needsUpdate = true; },
      undefined,
      () => console.warn('[record-store] art failed:', track.title),
    );
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
    setTex(t);
    return () => t.dispose();
  }, [anim, track.artwork_url]);

  useFrame(() => {
    if (meshRef.current) {
      const target = hover ? 1.06 : 1;
      meshRef.current.scale.x += (target - meshRef.current.scale.x) * 0.2;
      meshRef.current.scale.y = meshRef.current.scale.x;
    }
  });

  if (!tex) return null;
  const onClick = (e) => { e.stopPropagation(); audioEngine.playTrack(track); onPlay?.(track); };

  return (
    <group position={position} rotation={rotation}>
      {/* sleeve backing / glow frame */}
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[size + 0.08, size + 0.08]} />
        <meshBasicMaterial color={hover ? ACCENT : '#0b0b0e'} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh
        ref={meshRef}
        onClick={onClick}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial map={tex} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// The listening turntable — a console with a platter that carries the current
// pick's sleeve and spins while something is playing.
function Turntable({ nowPlaying, position = [0, 0, 0] }) {
  const platter = useRef();
  const [tex, setTex] = useState(null);
  useFrame((_, dt) => { if (platter.current && nowPlaying) platter.current.rotation.y += dt * 3.4; });

  useEffect(() => {
    setTex((old) => { old?.dispose?.(); return null; });
    if (!nowPlaying?.artwork_url) return undefined;
    const t = new THREE.TextureLoader().load(proxied(nowPlaying.artwork_url), (img) => { img.colorSpace = THREE.SRGBColorSpace; img.needsUpdate = true; });
    t.colorSpace = THREE.SRGBColorSpace;
    setTex(t);
    return () => t.dispose();
  }, [nowPlaying?.track_id, nowPlaying?.artwork_url]);

  return (
    <group position={position}>
      {/* cabinet */}
      <mesh castShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[1.5, 1.0, 1.1]} />
        <meshStandardMaterial color="#1b1620" roughness={0.6} metalness={0.35} />
      </mesh>
      {/* deck top */}
      <mesh position={[0, 1.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[1.4, 1.0]} />
        <meshStandardMaterial color="#0d0d12" roughness={0.5} metalness={0.4} />
      </mesh>
      {/* platter + record — lies flat on the deck (cylinder axis is vertical by
          default); the group spins it on Y. */}
      <group ref={platter} position={[-0.18, 1.03, 0]}>
        <mesh>
          <cylinderGeometry args={[0.42, 0.42, 0.03, 40]} />
          <meshStandardMaterial color="#08080b" roughness={0.35} metalness={0.5} emissive={ACCENT} emissiveIntensity={nowPlaying ? 0.12 : 0} />
        </mesh>
        {tex && (
          <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <circleGeometry args={[0.2, 32]} />
            <meshBasicMaterial map={tex} toneMapped={false} />
          </mesh>
        )}
      </group>
      {/* tonearm */}
      <mesh position={[0.5, 1.05, -0.32]} rotation={[0, nowPlaying ? -0.5 : -0.2, 0]}>
        <boxGeometry args={[0.05, 0.04, 0.6]} />
        <meshStandardMaterial color="#c9c9d2" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

// A featured sleeve with its own persisted transform. In ?cards=edit the
// SELECTED card listens for keys: ←→ move · ↑↓ height · w/s depth · [ ] rotate ·
// -/= size · r reset — auto-saving + logging a paste-ready line for CARD_DEFAULTS.
function EditableCard({ track, index, selected, onPlay }) {
  const key = `card:record-store:${index}`;
  const def = CARD_DEFAULTS[index] || CARD_DEFAULTS[0];
  const [xf, setXf] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(key) || 'null'); if (s) return s; } catch { /* noop */ }
    return def;
  });
  const camRef = useRef();
  useFrame(({ camera }) => { camRef.current = camera; });

  useEffect(() => {
    if (!CARDS_EDIT || !selected) return undefined;
    const onKey = (e) => {
      setXf((s) => {
        const [x, y, z] = s.pos;
        let fx = 0, fz = -1, rx = 1, rz = 0;
        const cam = camRef.current;
        if (cam) { const d = new THREE.Vector3(); cam.getWorldDirection(d); d.y = 0; if (d.lengthSq() > 1e-6) { d.normalize(); fx = d.x; fz = d.z; rx = -d.z; rz = d.x; } }
        const step = e.shiftKey ? 0.02 : 0.08;
        let next = s;
        if (e.key === 'ArrowLeft') next = { ...s, pos: [x - rx * step, y, z - rz * step] };
        else if (e.key === 'ArrowRight') next = { ...s, pos: [x + rx * step, y, z + rz * step] };
        else if (e.key === 'ArrowUp') next = { ...s, pos: [x, y + step, z] };        // height up
        else if (e.key === 'ArrowDown') next = { ...s, pos: [x, y - step, z] };       // height down
        else if (e.key === 'w') next = { ...s, pos: [x + fx * step, y, z + fz * step] };  // depth toward view
        else if (e.key === 's') next = { ...s, pos: [x - fx * step, y, z - fz * step] };  // depth away
        else if (e.key === '[') next = { ...s, rotY: s.rotY + 0.06 };
        else if (e.key === ']') next = { ...s, rotY: s.rotY - 0.06 };
        else if (e.key === '-') next = { ...s, size: Math.max(0.15, s.size - 0.04) };
        else if (e.key === '=') next = { ...s, size: s.size + 0.04 };
        else if (e.key === 'r') { localStorage.removeItem(key); return def; }
        else return s;
        e.preventDefault();
        const r = (v) => Math.round(v * 100) / 100;
        localStorage.setItem(key, JSON.stringify(next));
        console.log(`{ pos: [${next.pos.map(r).join(', ')}], rotY: ${r(next.rotY)}, size: ${r(next.size)} },  // card ${index}`);
        return next;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, index]);

  return (
    <group position={xf.pos} rotation={[0, xf.rotY, 0]}>
      {CARDS_EDIT && selected && (
        <mesh position={[0, 0, -0.03]}>
          <planeGeometry args={[xf.size + 0.14, xf.size + 0.14]} />
          <meshBasicMaterial color={ACCENT} toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
      )}
      <AlbumArt track={track} size={xf.size} onPlay={onPlay} />
    </group>
  );
}

// Interactive layer inside the photoreal splat: clickable featured releases +
// a now-playing readout. The splat supplies the shop; this is what you touch.
// (A carousel was tried here and reverted — placement TBD.)
function SplatOverlay({ tracks, nowPlaying, onPlay }) {
  const featured = useMemo(() => tracks.slice(0, CARD_DEFAULTS.length), [tracks]);
  const [sel, setSel] = useState(0);

  useEffect(() => {
    if (!CARDS_EDIT) return undefined;
    const onKey = (e) => { const n = parseInt(e.key, 10); if (!isNaN(n) && n >= 1 && n <= featured.length) setSel(n - 1); };
    window.addEventListener('keydown', onKey);
    console.log('[cards] edit mode — 1..N select · ←→ move · ↑↓ height · w/s depth · [ ] rotate · -/= size · r reset');
    return () => window.removeEventListener('keydown', onKey);
  }, [featured.length]);

  return (
    <group>
      {featured.map((t, i) => (
        <EditableCard key={t.track_id} track={t} index={i} selected={CARDS_EDIT && sel === i} onPlay={onPlay} />
      ))}
      <Text position={[0, 1.05, -0.6]} fontSize={0.1} color="#c8c8d0" anchorX="center">
        {nowPlaying ? `♪ ${nowPlaying.title}` : 'tap to play'}
      </Text>
      <pointLight position={[0, 2.0, -0.6]} color={ACCENT} intensity={3} distance={6} />
      {CARDS_EDIT && (
        <Html position={[0, 2.3, -0.6]} center style={{ pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(12,12,16,0.85)', color: '#9dff7d', font: '11px ui-monospace, monospace', padding: '4px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
            cards ?edit · card {sel + 1}/{featured.length} · 1..N select · ←→ move · ↑↓ height · w/s depth · [ ] rot · -/= size · r reset
          </div>
        </Html>
      )}
    </group>
  );
}

// Full procedural shop — the fallback shell that renders until (and if) the
// Marble splat loads, so the venue is never an empty void. This is the same
// hand-built "Wax & Vinyl" room, now demoted to fallback duty.
function ProceduralShop({ tracks, nowPlaying, onPlay }) {
  const wall = useMemo(() => tracks.slice(0, 8), [tracks]);
  const crate = useMemo(() => {
    const rest = tracks.length > 8 ? tracks.slice(8) : tracks;
    return rest.slice(0, 7);
  }, [tracks]);

  return (
    <group>
      {/* ── Shell ─────────────────────────────────────────────────────── */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[11, 10]} />
        <meshStandardMaterial color="#20161a" roughness={1} />
      </mesh>
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <planeGeometry args={[2.4, 9]} />
        <meshStandardMaterial color="#2c1f18" roughness={1} />
      </mesh>
      {[
        { p: [0, 2.3, -4.4], r: [0, 0, 0], s: [11, 4.6] },
        { p: [-5.5, 2.3, 0], r: [0, Math.PI / 2, 0], s: [10, 4.6] },
        { p: [5.5, 2.3, 0], r: [0, -Math.PI / 2, 0], s: [10, 4.6] },
        { p: [0, 2.3, 5], r: [0, Math.PI, 0], s: [11, 4.6] },
      ].map((w, i) => (
        <mesh key={i} position={w.p} rotation={w.r}>
          <planeGeometry args={w.s} />
          <meshStandardMaterial color="#2a1d24" roughness={1} />
        </mesh>
      ))}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 4.6, 0]}>
        <planeGeometry args={[11, 10]} />
        <meshStandardMaterial color="#140f13" roughness={1} />
      </mesh>

      {/* ── Neon sign ─────────────────────────────────────────────────── */}
      <mesh position={[0, 4.0, -4.35]}>
        <planeGeometry args={[6.2, 0.9]} />
        <meshBasicMaterial color="#0a0a0c" toneMapped={false} />
      </mesh>
      <Text position={[0, 4.0, -4.3]} fontSize={0.6} letterSpacing={0.08} color={ACCENT} anchorX="center" anchorY="middle" outlineWidth={0.01} outlineColor={ACCENT}>
        WAX &amp; VINYL
      </Text>

      {/* ── Back wall: animated featured releases ─────────────────────── */}
      {wall.map((t, i) => {
        const col = i % 4, row = Math.floor(i / 4);
        const x = -3.3 + col * 2.2;
        const y = 2.9 - row * 1.35;
        return <AlbumArt key={t.track_id} track={t} size={1.05} position={[x, y, -4.25]} onPlay={onPlay} />;
      })}

      {/* ── Front crate: flip-through sleeves ─────────────────────────── */}
      <group position={[-1.7, 0, 1.4]}>
        <mesh castShadow position={[0, 0.55, 0]}>
          <boxGeometry args={[2.4, 1.1, 1.0]} />
          <meshStandardMaterial color="#37271d" roughness={0.95} />
        </mesh>
        {crate.map((t, i) => (
          <AlbumArt
            key={t.track_id}
            track={t}
            size={0.86}
            allowVideo={false}
            position={[-0.9 + i * 0.28, 1.06, -0.05 + i * 0.02]}
            rotation={[-0.62, 0, 0]}
            onPlay={onPlay}
          />
        ))}
      </group>

      {/* ── Listening station ─────────────────────────────────────────── */}
      <Turntable nowPlaying={nowPlaying} position={[2.7, 0, -1.4]} />
      <Text position={[2.7, 1.85, -1.4]} fontSize={0.16} color="#9a9aa6" anchorX="center">
        {nowPlaying ? `♪ ${nowPlaying.title}` : 'pick a record'}
      </Text>

      {/* ── Light ─────────────────────────────────────────────────────── */}
      <ambientLight intensity={0.25} color="#40303a" />
      <pointLight position={[0, 4.2, -2]} color={ACCENT} intensity={9} distance={12} />
      <pointLight position={[-1.7, 3.2, 1.4]} color="#ffb25e" intensity={7} distance={9} castShadow />
      <pointLight position={[2.7, 2.6, -1.4]} color="#ff9a3c" intensity={5} distance={7} />
    </group>
  );
}

// The Record Store — "Wax & Vinyl". A blend of a photoreal Marble splat (the
// shop itself) and live 3D (the clickable featured records + turntable), the
// same recipe as the Listening Room. Everything is click-to-play via the
// single-source audio engine.
export default function RecordStore() {
  const [tracks, setTracks] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [splatReady, setSplatReady] = useState(false);
  const room = ROOMS['record-store'];

  useEffect(() => {
    api.featuredTracks().then((d) => setTracks(d?.tracks || [])).catch(() => setTracks([]));
  }, []);

  return (
    <group>
      {/* Photoreal Marble splat interior (when a manifest exists). */}
      {room.manifest && (
        <SplatWorld
          manifestUrl={room.manifest}
          // Don't push the manifest spawn — rooms.js spawn is authoritative here
          // so ?cam=edit "copy spawn" can re-aim the entry framing.
          onLoaded={() => setSplatReady(true)}
          onError={() => setSplatReady(false)}
        />
      )}

      {/* Blend: splat = room, overlay = the interactive parts. Until the splat
          is live (or if it 404s), the full procedural shop stands in. */}
      {splatReady
        ? <SplatOverlay tracks={tracks} nowPlaying={nowPlaying} onPlay={setNowPlaying} />
        : <ProceduralShop tracks={tracks} nowPlaying={nowPlaying} onPlay={setNowPlaying} />}
    </group>
  );
}
